/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useState} from 'react';

const rowsLimitKey = 'global_table_rows';
const preferModalKey = 'global_table_prefer_modal';
const reapplyFilterKey = 'global_table_reapply_filter';
const reapplygroupingKey = 'global_table_reapply_grouping';
const doubleClickKey = 'global_table_double_click';

export interface SuperTableOptions {
  rowsLimit: number;
  preferModal: boolean;
  reapplyFilter: boolean;
  reapplygrouping: boolean;
  lastFilter: any | null;
  lastGrouping: any | null;
  doubleClickToOpen: boolean;
}

export const ROWS_OPTIONS = [500, 1000, 2500, 5000];

const DEFAULT_OPTIONS: SuperTableOptions = {
  rowsLimit: ROWS_OPTIONS[0],
  preferModal: true,
  reapplyFilter: true,
  reapplygrouping: true,
  lastFilter: null,
  lastGrouping: null,
  doubleClickToOpen: true,
};

function getInitialOptions(tableId: string): SuperTableOptions {
  const lastFilterKey = `table_${tableId}_last_filter`;
  const lastGrouppingKey = `table_${tableId}_last_grouping`;

  const storedRows = localStorage.getItem(rowsLimitKey);
  const storedPrefer = localStorage.getItem(preferModalKey);
  const storedReapply = localStorage.getItem(reapplyFilterKey);
  const storedReapplyGroupping = localStorage.getItem(reapplygroupingKey);
  const storedDoubleClick = localStorage.getItem(doubleClickKey);
  const storedLastFilter = localStorage.getItem(lastFilterKey);
  const storedLastGroupping = localStorage.getItem(lastGrouppingKey);

  return {
    rowsLimit: storedRows ? Number(storedRows) : DEFAULT_OPTIONS.rowsLimit,
    preferModal: storedPrefer ? storedPrefer === 'true' : DEFAULT_OPTIONS.preferModal,
    reapplyFilter: storedReapply ? storedReapply === 'true' : DEFAULT_OPTIONS.reapplyFilter,
    reapplygrouping: storedReapplyGroupping ? storedReapplyGroupping === 'true' : DEFAULT_OPTIONS.reapplygrouping,
    doubleClickToOpen: storedDoubleClick ? storedDoubleClick === 'true' : DEFAULT_OPTIONS.doubleClickToOpen,
    lastFilter: storedLastFilter ? JSON.parse(storedLastFilter) : DEFAULT_OPTIONS.lastFilter,
    lastGrouping: storedLastGroupping ? JSON.parse(storedLastGroupping) : DEFAULT_OPTIONS.lastGrouping,
  };
}

export function useSuperTableOptions(tableId: string): [SuperTableOptions, (options: SuperTableOptions) => void] {
  const [options, setOptions] = useState<SuperTableOptions>(() => getInitialOptions(tableId));
  const lastFilterKey = `table_${tableId}_last_filter`;
  const lastGrouppingKey = `table_${tableId}_last_grouping`;

  const updateOptions = (newOptions: SuperTableOptions) => {
    localStorage.setItem(rowsLimitKey, newOptions.rowsLimit.toString());
    localStorage.setItem(preferModalKey, newOptions.preferModal.toString());
    localStorage.setItem(reapplyFilterKey, newOptions.reapplyFilter.toString());
    localStorage.setItem(reapplygroupingKey, newOptions.reapplygrouping.toString());
    localStorage.setItem(doubleClickKey, newOptions.doubleClickToOpen.toString());
    localStorage.setItem(lastFilterKey, JSON.stringify(newOptions.lastFilter));
    localStorage.setItem(lastGrouppingKey, JSON.stringify(newOptions.lastGrouping));

    setOptions(newOptions);
  };

  return [options, updateOptions];
}
